import { Button, Checkbox, Divider, FormControlLabel, TextField } from '@material-ui/core';
import BasicModal from 'components/BasicModal';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import {
    ALL_CATEGORIES,
    COST_REPORT_CATEGORY_LABELS,
    DATE_RANGE_PRESET_LABELS,
    DATE_RANGE_PRESETS,
    LOCAL_STORAGE_CATEGORIES_KEY,
} from './costReportExportConsts';
import { useCostReportExportModalStyles } from './CostReportExportModalStyles';

const getPresetDates = (preset) => {
    const now = dayjs();
    if (preset === DATE_RANGE_PRESETS.THIS_MONTH) {
        return {
            dateStart: now.startOf('month').format('YYYY-MM-DD'),
            dateEnd: now.endOf('month').format('YYYY-MM-DD'),
        };
    }
    if (preset === DATE_RANGE_PRESETS.LAST_MONTH) {
        const last = now.subtract(1, 'month');
        return {
            dateStart: last.startOf('month').format('YYYY-MM-DD'),
            dateEnd: last.endOf('month').format('YYYY-MM-DD'),
        };
    }
    return null;
};

const loadSavedCategories = () => {
    try {
        const saved = localStorage.getItem(LOCAL_STORAGE_CATEGORIES_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
    } catch (_) {}
    return ALL_CATEGORIES;
};

const saveCategories = (categories) => {
    try {
        localStorage.setItem(LOCAL_STORAGE_CATEGORIES_KEY, JSON.stringify(categories));
    } catch (_) {}
};

const CostReportExportModal = ({ open, onClose, onExport, isExporting }) => {
    const classes = useCostReportExportModalStyles();

    const [preset, setPreset] = useState(DATE_RANGE_PRESETS.THIS_MONTH);
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [selectedCategories, setSelectedCategories] = useState(loadSavedCategories);

    useEffect(() => {
        if (preset !== DATE_RANGE_PRESETS.CUSTOM) {
            const dates = getPresetDates(preset);
            setDateStart(dates.dateStart);
            setDateEnd(dates.dateEnd);
        }
    }, [preset]);

    const handlePreset = (value) => setPreset(value);

    const handleCustomDateStart = (e) => {
        setPreset(DATE_RANGE_PRESETS.CUSTOM);
        setDateStart(e.target.value);
    };

    const handleCustomDateEnd = (e) => {
        setPreset(DATE_RANGE_PRESETS.CUSTOM);
        setDateEnd(e.target.value);
    };

    const toggleCategory = (category) => {
        setSelectedCategories((prev) => {
            const next = prev.includes(category)
                ? prev.filter((c) => c !== category)
                : [...prev, category];
            saveCategories(next);
            return next;
        });
    };

    const selectAll = () => { setSelectedCategories(ALL_CATEGORIES); saveCategories(ALL_CATEGORIES); };
    const selectNone = () => { setSelectedCategories([]); saveCategories([]); };

    const allSelected = selectedCategories.length === ALL_CATEGORIES.length;
    const noneSelected = selectedCategories.length === 0;
    const canExport = !noneSelected && dateStart && dateEnd && dateEnd >= dateStart;

    const handleConfirm = () => {
        onExport({ dateStart, dateEnd, categories: selectedCategories });
    };

    const content = (
        <div className={classes.modalContent}>
            {/* Date range */}
            <div className={classes.section}>
                <div className={classes.sectionLabel}>Date Range</div>
                <div className={classes.presetGroup}>
                    {[DATE_RANGE_PRESETS.THIS_MONTH, DATE_RANGE_PRESETS.LAST_MONTH].map((p) => (
                        <Button
                            key={p}
                            variant={preset === p ? 'contained' : 'outlined'}
                            color="primary"
                            size="small"
                            className={classes.presetButton}
                            onClick={() => handlePreset(p)}
                        >
                            {DATE_RANGE_PRESET_LABELS[p]}
                        </Button>
                    ))}
                </div>
                <div className={classes.dateRow}>
                    <TextField
                        label="Start date"
                        type="date"
                        size="small"
                        variant="outlined"
                        value={dateStart}
                        onChange={handleCustomDateStart}
                        className={classes.dateField}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: dateEnd }}
                    />
                    <TextField
                        label="End date"
                        type="date"
                        size="small"
                        variant="outlined"
                        value={dateEnd}
                        onChange={handleCustomDateEnd}
                        className={classes.dateField}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: dateStart }}
                    />
                </div>
            </div>

            <Divider className={classes.divider} />

            {/* Categories */}
            <div className={classes.section}>
                <div className={classes.sectionLabel}>Cost Categories</div>
                <div className={classes.categoryRow}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={allSelected}
                                indeterminate={!allSelected && !noneSelected}
                                onChange={allSelected ? selectNone : selectAll}
                                color="primary"
                                size="small"
                            />
                        }
                        label={<span className={classes.categoryLabel}><strong>All</strong></span>}
                    />
                </div>
                <div className={classes.categoriesGrid}>
                    {ALL_CATEGORIES.map((category) => (
                        <div key={category} className={classes.categoryRow}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectedCategories.includes(category)}
                                        onChange={() => toggleCategory(category)}
                                        color="primary"
                                        size="small"
                                    />
                                }
                                label={<span className={classes.categoryLabel}>{COST_REPORT_CATEGORY_LABELS[category]}</span>}
                            />
                        </div>
                    ))}
                </div>
            </div>

            <div className={classes.actions}>
                <Button variant="outlined" onClick={onClose} disabled={isExporting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!canExport || isExporting}
                    onClick={handleConfirm}
                >
                    {isExporting ? 'Exporting…' : 'Export PDF'}
                </Button>
            </div>
        </div>
    );

    return (
        <BasicModal
            open={open}
            header="Export Cost Report"
            handleClose={onClose}
            component={content}
        />
    );
};

export default CostReportExportModal;
